import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workersAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, User, Wrench, Truck, Crown,
  ChevronRight, Clock, Calendar, IndianRupee
} from 'lucide-react';

const ROLE_CONFIG = {
  karigar: { label: 'Karigar', icon: Wrench, color: 'var(--brand-navy)', bg: 'rgba(27,42,74,0.08)' },
  master: { label: 'Master', icon: Crown, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  outsourced_vendor: { label: 'Outsourced', icon: Truck, color: '#0E7490', bg: 'rgba(14,116,144,0.08)' },
};

export default function WorkersListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const canEdit = ['Admin', 'Production'].includes(user?.role);

  useEffect(() => {
    fetchWorkers();
  }, [roleFilter]);

  const fetchWorkers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      const res = await workersAPI.list(params);
      setWorkers(res.data);
    } catch (err) {
      toast.error('Failed to load workers');
    } finally {
      setLoading(false);
    }
  };

  const filtered = workers.filter(w =>
    !search || w.name.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {
    karigar: filtered.filter(w => w.role === 'karigar'),
    master: filtered.filter(w => w.role === 'master'),
    outsourced_vendor: filtered.filter(w => w.role === 'outsourced_vendor'),
  };

  return (
    <div className="space-y-6" data-testid="workers-list-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
            Team
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            {workers.length} worker{workers.length !== 1 ? 's' : ''} {'\u00b7'} Karigars, Masters & Vendors
          </p>
        </div>
        {canEdit && (
          <Button data-testid="add-worker-button" onClick={() => navigate('/app/production/workers/new')}
            className="h-10 rounded-xl px-5 text-sm font-medium" style={{ backgroundColor: 'var(--brand-navy)', color: 'var(--bg)' }}>
            <Plus className="w-4 h-4 mr-2" /> Add Worker
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input
            data-testid="worker-search-input"
            placeholder="Search by name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 rounded-lg text-sm"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px] h-10 rounded-lg text-sm" data-testid="worker-role-filter">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="karigar">Karigars</SelectItem>
            <SelectItem value="master">Masters</SelectItem>
            <SelectItem value="outsourced_vendor">Outsourced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      )}

      {/* Worker Groups */}
      {!loading && filtered.length === 0 && (
        <Card className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-10 text-center">
            <User className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--brand-navy)', opacity: 0.3 }} strokeWidth={1.5} />
            <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>No workers found</p>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Try adjusting your search or filters</p>
          </CardContent>
        </Card>
      )}

      {!loading && Object.entries(grouped).map(([role, items]) => {
        if (items.length === 0 || (roleFilter !== 'all' && roleFilter !== role)) return null;
        const config = ROLE_CONFIG[role];
        return (
          <div key={role} className="space-y-3">
            <div className="flex items-center gap-2">
              <config.icon className="w-4 h-4" style={{ color: config.color }} />
              <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: config.color }}>
                {config.label}s
              </h2>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 rounded-md ml-1">
                {items.length}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(worker => (
                <WorkerCard key={worker.id} worker={worker} config={config} onClick={() => navigate(`/app/production/workers/${worker.id}`)} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WorkerCard({ worker, config, onClick }) {
  const initials = worker.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Card
      data-testid={`worker-card-${worker.id}`}
      className="rounded-xl border border-border/80 bg-white hover:shadow-[0_2px_16px_rgba(16,24,40,0.08)] cursor-pointer group"
      style={{ transition: 'box-shadow 200ms, border-color 200ms' }}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {worker.photo_url ? (
              <img src={worker.photo_url} alt={worker.name}
                className="w-11 h-11 rounded-full object-cover border" style={{ borderColor: 'var(--border-color)' }} />
            ) : (
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold"
                style={{ backgroundColor: config.bg, color: config.color }}>
                {initials}
              </div>
            )}
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>{worker.name}</h3>
              <Badge variant="outline" className="text-[10px] font-medium px-1.5 py-0 rounded-md mt-0.5"
                style={{ backgroundColor: config.bg, color: config.color, borderColor: 'transparent' }}>
                {config.label}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${worker.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
              {worker.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Capabilities */}
        {worker.capabilities && worker.capabilities.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {worker.capabilities.slice(0, 4).map(cap => {
              const pieceRate = worker.role === 'outsourced_vendor' && worker.piece_rate_per_garment
                ? worker.piece_rate_per_garment[cap.garment_type] : null;
              return (
                <div key={cap.id} className="flex items-center justify-between text-[11px]">
                  <span style={{ color: 'var(--navy-ink)' }}>{cap.garment_type_label}</span>
                  <span className="tabular-nums font-medium" style={{ color: 'var(--ink-muted)' }}>
                    {pieceRate ? `\u20b9${pieceRate.toLocaleString('en-IN')}/pc` : `${cap.output_per_day}/day`}
                    {' \u00b7 '}{cap.hours_per_unit}hrs
                  </span>
                </div>
              );
            })}
            {worker.capabilities.length > 4 && (
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>+{worker.capabilities.length - 4} more</p>
            )}
          </div>
        )}

        {/* Piece rate summary for vendors without capabilities listed */}
        {worker.role === 'outsourced_vendor' && worker.piece_rate_per_garment
          && Object.keys(worker.piece_rate_per_garment).length > 0
          && (!worker.capabilities || worker.capabilities.length === 0) && (
          <div className="mb-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(14,116,144,0.04)' }}>
            <p className="text-[10px] font-medium" style={{ color: '#0E7490' }}>
              <IndianRupee className="w-3 h-3 inline mr-0.5" />
              {Object.keys(worker.piece_rate_per_garment).length} garment types priced
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border/40">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" style={{ color: 'var(--ink-muted)' }} />
              <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{worker.shift_hours}hr shift</span>
            </div>
            {worker.overtime_rate_per_hour && (
              <span className="text-[10px] tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                OT {'\u20b9'}{worker.overtime_rate_per_hour}/hr
              </span>
            )}
          </div>
          {worker.is_temporary && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" style={{ color: 'var(--warning)' }} />
              <span className="text-[10px] font-medium" style={{ color: 'var(--warning)' }}>Temporary</span>
            </div>
          )}
          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-60" style={{ color: 'var(--ink-muted)', transition: 'opacity 150ms' }} />
        </div>
      </CardContent>
    </Card>
  );
}
